/**
 * System rows — canonical system-row definitions + builder.
 *
 * System rows (header, footer, all-day events, debug rows,
 * calendar-system sub-rows) live outside the user tree but ride
 * alongside during render. This module ports `tento/tento-lona-js/src/system-
 * rows.ts` into tento/tento-lona-js/sheets per §8.8 — with one shape difference:
 *
 *   - lona-js's `BuildConfig` pulled `calendarSystems` from
 *     preferences via an injected callback. The port takes a
 *     pure `SystemRowsConfig` parameter instead. lona-js's wrapper
 *     (thin shim in `tento/tento-lona-js/src/client/client.ts`) reads preferences up
 *     front and passes the resolved list to `buildSystemRows`.
 *
 *   - `ROW_TYPE_SPECS` replaces `RowType` (same
 *     `defaultAttributes` + `childSpecs` contract, looked up by
 *     wire type).
 *
 *   - `ViewModelRow` shrinks to a narrow local `TreeRowLike`
 *     interface — the port doesn't pull in the yuzu-ui viewmodel.
 *
 * Output shape matches lona-js exactly — `SystemRowDef` is
 * byte-compatible so consumers can swap the import path without
 * touching call sites.
 */

import type { Option } from "../core/option.ts";
import { Rational } from "@tento-core/rational";

import { SheetsNetwork } from "../wire/rows-network.ts";
import { RowId } from "../ids/row-id.ts";
import { RowNetworkKey } from "../ids/row-network-key.ts";
import {
  type ChildSpec,
  ROW_TYPE_SPECS,
  RowTypeSpecOps,
} from "./row-type-spec.ts";
import { Json, type JsonObject } from "../core/json.ts";
import { SheetRegistries } from "../sheet/sheet-registries.ts";
import { UserId } from "../ids/ids.ts";
import type { Timescale } from "../render/timescale.ts";
import { deepFrozen } from "../core/frozen-collections.ts";

// ── Types ──────────────────────────────────────────────────────────

/**
 * Minimal row shape `buildChildRows` reads from the network input
 * — id, type, ownerId, attributes. Using `SheetsNetwork.SheetRow` works
 * too; this alias lets callers pass partials without manufacturing
 * full network rows.
 */
export interface SystemRowInput {
  readonly id: { toString(): string };
  readonly type: string;
  readonly ownerId: UserId;
  readonly attributes?: Json;
}

/**
 * A system row def — the network row shape plus the system-row
 * marker bits. Matches lona-js's `SystemRowDef` byte-for-byte.
 */
export interface SystemRowDef extends SheetsNetwork.SheetRowDb {
  position: Rational;
  /** A row the sheet builds for itself (the SDK row's `SheetRow.Kind`). */
  kind: "system";
  /** Whether the row's legend shows (absent: `"shown"`). */
  legend?: "shown" | "hidden";
  children?: RowId[];
}

/** The system rows `SystemRows.build` made for a sheet: the header, footer and chrome rows, in order. */
export interface SystemRowsResult {
  rows: SystemRowDef[];
}

/**
 * What a sheet's second header row shows: a secondary calendar system, or the
 * month and weekday.
 */
export type HeaderSecondRowKind = "calendar-system" | "month-weekday";

/**
 * Canonical positions for synthetic system rows.
 *
 * Keep this list static and explicit: system-row ordering is still
 * the normal rational-position sort, and each projected/system row
 * should state the position it belongs to instead of relying on
 * factory call order or renderer layering.
 */
export namespace SystemRowPosition {
  export const HEADER_SURFACE: Rational = Object.freeze(Rational.nth(-3));
  export const HEADER_PRIMARY: Rational = Object.freeze(Rational.nth(0));
  export const HEADER_AUXILIARY: Rational = Object.freeze(Rational.nth(1));
  export const HEADER_PROJECTED_CALENDAR_SYSTEM: Rational = Object.freeze(Rational.nth(100));
  export const HEADER_PROJECTED_SECONDARY_CALENDAR: Rational = Object.freeze(Rational.nth(200));
  export const HEADER_PROJECTED_MONTH_WEEKDAY: Rational = Object.freeze(Rational.nth(300));
  export const HEADER_SECOND_ROW_CALENDAR_SYSTEM: typeof HEADER_PROJECTED_CALENDAR_SYSTEM =
    HEADER_PROJECTED_CALENDAR_SYSTEM;
  export const HEADER_SECOND_ROW_SECONDARY_CALENDAR: typeof HEADER_PROJECTED_SECONDARY_CALENDAR =
    HEADER_PROJECTED_SECONDARY_CALENDAR;
  export const HEADER_SECOND_ROW_MONTH_WEEKDAY: typeof HEADER_PROJECTED_MONTH_WEEKDAY = HEADER_PROJECTED_MONTH_WEEKDAY;

  export const FOOTER_TIMELINE_LEGEND: Rational = Object.freeze(Rational.nth(999997));
  export const FOOTER_EPHEMERAL_DISCLAIMER: Rational = Object.freeze(Rational.nth(999998));
  export const FOOTER_PRIMARY: Rational = Object.freeze(Rational.nth(999999));
  export const FOOTER_SURFACE: Rational = Object.freeze(Rational.nth(1000000));
  export const FOOTER_SURFACE_TIMELINE_LEGEND: Rational = Object.freeze(Rational.nth(0));
  export const FOOTER_SURFACE_EPHEMERAL_DISCLAIMER: Rational = Object.freeze(Rational.nth(1));
  export const FOOTER_SURFACE_PRIMARY: Rational = Object.freeze(Rational.nth(2));
}

/**
 * What `SystemRows.build` makes a sheet's system rows (headers, footers,
 * banners, debug rows) from. It carries the sheet's mode, catalog and
 * debug rows, its calendar systems and registries. Pure data — preferences are resolved by
 * the caller (the SDK reads `CalendarPreferences` and passes the list in).
 */
export interface SystemRowsConfig {
  sheet?: { rows: readonly SystemRowInput[] };
  /** The debug system rows to build (absent: none). */
  debugRows?: readonly SystemRowsConfig.DebugRow[];
  /**
   * What the sheet is (absent: `"persisted"`). A sheet that is not
   * persisted shows the "unsaved" disclaimer banner unless its `catalog` is
   * `"builtin"`; a `"template"` one gets the "Use this template" claim copy,
   * an `"ephemeral"` one the generic "save to your sheets" copy. Both CTAs
   * promote the sheet identically — the only difference is wording. The app
   * computes the mode (it owns the template catalog); the SDK picks the copy.
   */
  mode?: SystemRowsConfig.Mode;
  /**
   * Which catalog the sheet belongs to (absent: `"user"`). A `"builtin"`
   * (reserved catalog) sheet — `:~lona:home`, `:calendar`, … — is always
   * present in the sidebar, so it suppresses the "unsaved" disclaimer even
   * before it is promoted: the banner is only meaningful for user-minted
   * sheets that could be lost.
   */
  catalog?: SystemRowsConfig.Catalog;
  calendarSystems?: readonly string[];
  /**
   * The lookup tables the sheet's rows resolve through (the sheet's
   * `Sheet.registries`, its client's `ClientConfig.registries`). Absent —
   * the built-ins (`SheetRegistries.BUILTINS`).
   */
  registries?: SheetRegistries;
}

/** Types that go with `SystemRowsConfig`: `Mode`, `Catalog`, `DebugRow`. */
export namespace SystemRowsConfig {
  /**
   * What a sheet is: `"persisted"` (a server sheet), `"ephemeral"` (a local
   * sheet not yet saved) or `"template"` (an ephemeral sheet opened from a
   * template).
   */
  export type Mode = "persisted" | "ephemeral" | "template";

  /** Which catalog a sheet belongs to: the reserved `"builtin"` one or the user's. */
  export type Catalog = "builtin" | "user";

  /**
   * A debug system row: the scroll-info header row (`"scroll-info"`) or the
   * timeline-legend footer row (`"timeline-legend"`).
   */
  export type DebugRow = "scroll-info" | "timeline-legend";
}

/**
 * Narrow row shape `addTreeChildRows` + `applySecondHeaderRows`
 * read. yuzu-ui's full `ViewModelRow` satisfies this structurally;
 * the port avoids the dep so consumers outside the yuzu stack can
 * satisfy it directly.
 */
export interface TreeRowLike {
  readonly id: { toString(): string };
  readonly ownerId: UserId;
  /** The row's attribute bag (read for the `children` visibility map). */
  attributes(): Option<JsonObject>;
  /** The row's child seeds; `placement: "root"` ones become system rows. */
  readonly childSpecs: readonly ChildSpec[];
  headerSecondRowKind?(
    sheetTimescale: Timescale,
  ): Option<HeaderSecondRowKind>;
  requiresHeaderSecondRow(sheetTimescale: Timescale): boolean;
}

/**
 * Types and values that go with `SystemRowDef`: `Fields`, `of`, `makeId`,
 * `SurfaceBundle`, `FooterBundle`, `HeaderBundle`, and 2 more.
 */
export namespace SystemRowDef {
  /**
   * What `of` takes: every key of the wire row (`null` stated, like a
   * generated `of`) and the system-row marker bits.
   */
  export type Fields =
    & SheetsNetwork.SheetRowDb
    & Omit<SystemRowDef, keyof SheetsNetwork.SheetRowDb>;

  /** A system row def: the wire row checked by its `of`, with the marker bits. */
  export function of(fields: Fields): SystemRowDef {
    return { ...SheetsNetwork.SheetRowDb.make(fields), ...fields };
  }

  /** Construct a canonical `system(...)` row id. */
  export function makeId(name: string, param: Option<string>): RowId {
    return param !== null && param !== undefined
      ? RowId.of(`system(${name}|${param})`)
      : RowId.of(`system(${name})`);
  }

  /** The synthetic header / footer surface-containers. */
  export interface SurfaceBundle {
    header: SystemRowDef;
    footer: SystemRowDef;
  }

  /** The static footer system rows. */
  export interface FooterBundle {
    footer: SystemRowDef;
    timelineLegend: SystemRowDef;
    ephemeralDisclaimer: SystemRowDef;
  }

  /** The static header system rows. */
  export interface HeaderBundle {
    header: SystemRowDef;
    dayOfMonth: SystemRowDef;
    index: SystemRowDef;
  }

  export namespace Static {
    /**
     * Synthetic surface-containers — top-level transparent wrappers for header
     * and footer surfaces. Children are the existing synthetic system rows.
     *
     * On migrated sheets the server-backed surface-container at top level
     * supersedes these synthetic rows; `Sheet.replaceSystemRows` reparents the
     * children under the server/authored anchor.
     */
    export const Surfaces: SurfaceBundle = {
      header: SystemRowDef.of({
        id: makeId("surface", "header"),
        type: "surface-container",
        metadata: { surface: "header" },
        kind: "system",
        position: SystemRowPosition.HEADER_SURFACE,
        ownerId: UserId.SYSTEM,
        timeScale: "custom",
        attributes: null,
        formula: null,
        heightPx: null,
        icon: null,
        label: null,
        lookupKey: null,
      }),
      footer: SystemRowDef.of({
        id: makeId("surface", "footer"),
        type: "surface-container",
        metadata: { surface: "footer" },
        kind: "system",
        position: SystemRowPosition.FOOTER_SURFACE,
        ownerId: UserId.SYSTEM,
        timeScale: "custom",
        attributes: null,
        formula: null,
        heightPx: null,
        icon: null,
        label: null,
        lookupKey: null,
      }),
    };

    export const Footers: FooterBundle = {
      footer: SystemRowDef.of({
        id: makeId("footer", null),
        type: "sheet-footer",
        kind: "system",
        position: SystemRowPosition.FOOTER_PRIMARY,
        ownerId: UserId.SYSTEM,
        timeScale: "custom",
        legend: "shown",
        label: "",
        attributes: null,
        formula: null,
        heightPx: null,
        icon: null,
        lookupKey: null,
        metadata: null,
      }),
      timelineLegend: SystemRowDef.of({
        id: makeId("footer", "timeline-legend"),
        type: "timeline-legend",
        kind: "system",
        legend: "hidden",
        position: SystemRowPosition.FOOTER_TIMELINE_LEGEND,
        ownerId: UserId.SYSTEM,
        timeScale: "custom",
        label: "",
        attributes: null,
        formula: null,
        heightPx: null,
        icon: null,
        lookupKey: null,
        metadata: null,
      }),
      ephemeralDisclaimer: SystemRowDef.of({
        id: makeId("footer", "disclaimer"),
        type: "label",
        kind: "system",
        legend: "hidden",
        position: SystemRowPosition.FOOTER_EPHEMERAL_DISCLAIMER,
        ownerId: UserId.SYSTEM,
        timeScale: "custom",
        label: "",
        attributes: {
          // Generic (non-template) ephemeral copy. Template-derived sheets
          // override `content` + the CTA label in `makeEphemeralBanner`;
          // both CTAs run the same `saveEphemeral` promote.
          content:
            "Unsaved draft — your edits are saved to your sheets automatically.",
          editable: false,
          hasBackground: true,
          // Disclaimer stays centered; the LabelInertPlugin default is left-aligned.
          textAlign: "center",
          ctaPlacement: "below",
          ctas: [
            {
              id: "save-ephemeral",
              label: "Save to your sheets",
              style: "primary",
              action: {
                type: "named",
                name: "saveEphemeral",
              },
            },
          ],
        },
        formula: null,
        heightPx: null,
        icon: null,
        lookupKey: null,
        metadata: null,
      }),
    };

    export const HeaderRows: HeaderBundle = {
      header: SystemRowDef.of({
        id: makeId("header", null),
        type: "header",
        kind: "system",
        position: SystemRowPosition.HEADER_PRIMARY,
        ownerId: UserId.SYSTEM,
        timeScale: "custom",
        attributes: null,
        formula: null,
        heightPx: null,
        icon: null,
        label: null,
        lookupKey: null,
        metadata: null,
      }),
      dayOfMonth: SystemRowDef.of({
        id: makeId("dom", null),
        type: "formula",
        kind: "system",
        formula: "date.dom.range",
        heightPx: 24,
        legend: "hidden",
        attributes: {
          style: "flat",
          size: "small",
        },
        position: SystemRowPosition.HEADER_AUXILIARY,
        ownerId: UserId.SYSTEM,
        timeScale: "custom",
        icon: null,
        label: null,
        lookupKey: null,
        metadata: null,
      }),
      index: SystemRowDef.of({
        id: makeId("dbg", null),
        type: "formula",
        kind: "system",
        formula: "render.offset",
        heightPx: 24,
        legend: "hidden",
        attributes: {
          style: "flat",
          size: "small",
        },
        position: SystemRowPosition.HEADER_AUXILIARY,
        ownerId: UserId.SYSTEM,
        timeScale: "custom",
        icon: null,
        label: null,
        lookupKey: null,
        metadata: null,
      }),
    };
  }

  /**
   * The ephemeral banner row for this sheet. Template-derived sheets get a
   * "Use this template" claim banner; every other ephemeral sheet keeps the
   * generic "save to your sheets" copy baked into `Static.Footers`.
   */
  export function makeEphemeralBanner(origin: "template" | "blank"): SystemRowDef {
    const base = Static.Footers.ephemeralDisclaimer;
    if (origin !== "template") return base;
    return {
      ...base,
      attributes: {
        ...(Json.object(base.attributes) ?? {}),
        content:
          "You're previewing a template — use it to add an editable copy to your sheets.",
        ctas: [
          {
            id: "save-ephemeral",
            label: "Use this template",
            style: "primary",
            action: { type: "named", name: "saveEphemeral" },
          },
        ],
      },
    };
  }
}

/** The static surface rows every sheet carries. */
export const SURFACES_STATIC: typeof SystemRowDef.Static.Surfaces = deepFrozen(SystemRowDef.Static.Surfaces);
/** The static footer rows every sheet carries. */
export const FOOTERS_STATIC: typeof SystemRowDef.Static.Footers = deepFrozen(SystemRowDef.Static.Footers);
/** The static header rows every sheet carries. */
export const HEADERS_STATIC: typeof SystemRowDef.Static.HeaderRows = deepFrozen(SystemRowDef.Static.HeaderRows);

// ── Build System Rows ──────────────────────────────────────────────

export namespace SystemRowDef {
  /**
   * Build a single system row from a `ChildSpec`. Attribute values pointing at
   * the `PARENT_ROW_ID` sentinel are substituted with the parent row's id in
   * its wire spelling (`parentRowId`), which also names the system row's id.
   */
  export function makeSpecRow(
    spec: ChildSpec,
    parentRowId: RowNetworkKey,
    ownerId: UserId,
  ): SystemRowDef {
    const specAttrs = spec.attributes
      ? RowTypeSpecOps.resolveParentRowId(spec.attributes, parentRowId)
      : undefined;
    const row: SystemRowDef = SystemRowDef.of({
      id: SystemRowDef.makeId(spec.type, parentRowId),
      type: spec.type,
      kind: "system",
      position: spec.position,
      ownerId,
      timeScale: "custom",
      attributes: null,
      formula: null,
      heightPx: null,
      icon: null,
      label: null,
      lookupKey: null,
      metadata: null,
    });
    if (spec.label != null) row.label = spec.label;
    if (spec.formula != null) row.formula = spec.formula;
    if (spec.heightPx != null) row.heightPx = spec.heightPx;
    if (specAttrs) row.attributes = specAttrs;
    return row;
  }
}

/** SheetsNetwork-data-based root-placement children (invariant middle section). */
function buildChildRows(config: SystemRowsConfig): SystemRowDef[] {
  const rows: SystemRowDef[] = [];
  if (!config.sheet) return rows;
  for (const row of config.sheet.rows) {
    const attributes = Json.object(row.attributes ?? null);
    const input = {
      id: row.id.toString(),
      type: row.type,
      ...(attributes != null ? { attributes } : {}),
    };
    // Only registered row types seed system rows from here.
    const registries = config.registries ?? SheetRegistries.BUILTINS;
    if (registries.rowTypes.get(row.type) == null) continue;
    const childSpecs = RowTypeSpecOps.childSpecsFor(row.type, input, registries);
    if (childSpecs.length === 0) continue;
    const attrs = RowTypeSpecOps.reconcileForWireType(
      row.type,
      input,
      input.attributes ?? null,
      registries,
    ) ?? null;

    const parentIdStr = RowNetworkKey.fromWire(row.id.toString());
    for (const spec of RowTypeSpecOps.resolveChildren(childSpecs, attrs)) {
      if (spec.placement !== "root") continue;
      rows.push(SystemRowDef.makeSpecRow(spec, parentIdStr, row.ownerId));
    }
  }
  return rows;
}

export namespace SystemRowDef {
  export function findOrThrow(
    result: SystemRowsResult,
    id: RowId,
  ): SystemRowDef {
    const idStr = id.toString();
    const row = result.rows.find((r) => r.id.toString() === idStr) ?? null;
    if (row == null) {
      throw new Error(`SystemRows: missing child row "${idStr}".`);
    }
    return row;
  }

  export function find(
    result: SystemRowsResult,
    id: RowId,
  ): Option<SystemRowDef> {
    const idStr = id.toString();
    return result.rows.find((r) => r.id.toString() === idStr) ?? null;
  }

  export function headerProjectionParent(
    result: SystemRowsResult,
  ): Option<SystemRowDef> {
    return find(result, SystemRowDef.makeId("surface", "header")) ??
      find(result, SystemRowDef.makeId("header", null));
  }

  export function appendPositionedChild(
    result: SystemRowsResult,
    parent: SystemRowDef,
    childId: RowId,
  ): void {
    // Rows-side consumers can read `children` order directly, so keep it aligned
    // with the same rational positions used by the joined tree.
    const child = findOrThrow(result, childId);
    const childPosition = child.position;
    const children = [...(parent.children ?? [])];
    let insertAt = children.length;
    for (let i = 0; i < children.length; i++) {
      const existingId = children[i];
      if (existingId === undefined) continue;
      const existing = findOrThrow(result, existingId);
      const existingPosition = existing.position;
      if (Rational.compare(childPosition, existingPosition) < 0) {
        insertAt = i;
        break;
      }
    }
    children.splice(insertAt, 0, childId);
    parent.children = children;
  }

  export function makeProjectedHeaderRow(
    kind: HeaderSecondRowKind,
  ): SystemRowDef {
    const calendarSystem = kind === "month-weekday"
      ? "month-weekday"
      : "gregorian";
    return SystemRowDef.of({
      id: kind === "month-weekday"
        ? SystemRowDef.makeId("calendar-system", "month-weekday")
        : SystemRowDef.makeId("calendar-system", null),
      type: "calendar-system",
      kind: "system",
      position: kind === "month-weekday"
        ? SystemRowPosition.HEADER_SECOND_ROW_MONTH_WEEKDAY
        : SystemRowPosition.HEADER_SECOND_ROW_CALENDAR_SYSTEM,
      ownerId: UserId.SYSTEM,
      timeScale: "custom",
      legend: "hidden",
      attributes: { calendarSystem },
      formula: null,
      heightPx: null,
      icon: null,
      label: null,
      lookupKey: null,
      metadata: null,
    });
  }

  export function makeSecondaryCalendar(calendarSystem: string): SystemRowDef {
    return SystemRowDef.of({
      id: SystemRowDef.makeId("calendar-system", calendarSystem),
      type: "calendar-system",
      kind: "system",
      position: SystemRowPosition.HEADER_SECOND_ROW_SECONDARY_CALENDAR,
      ownerId: UserId.SYSTEM,
      timeScale: "custom",
      legend: "hidden",
      attributes: { calendarSystem, size: "xs" },
      formula: null,
      heightPx: null,
      icon: null,
      label: null,
      lookupKey: null,
      metadata: null,
    });
  }
}

/** Add a projected header row as a child of the header surface. */
function addSecondHeaderRow(
  result: SystemRowsResult,
  kind: HeaderSecondRowKind,
): void {
  const row = SystemRowDef.makeProjectedHeaderRow(kind);
  result.rows.push(row);
  const parent = SystemRowDef.headerProjectionParent(result);
  if (parent) {
    SystemRowDef.appendPositionedChild(result, parent, row.id);
  }
}

function headerSecondRowKindFor<
  R extends {
    headerSecondRowKind?(ts: Timescale): Option<HeaderSecondRowKind>;
    requiresHeaderSecondRow(ts: Timescale): boolean;
  },
>(row: R, timescale: Timescale): Option<HeaderSecondRowKind> {
  const kind = row.headerSecondRowKind?.(timescale);
  if (kind) return kind;
  return row.requiresHeaderSecondRow(timescale) ? "calendar-system" : null;
}

/**
 * Add root-placement children from live tree rows into the result.
 *
 * Exported because a consumer-defined `SystemRow.Factory` must reproduce
 * the invariant middle of the row set in its own `onRowsBuilt`; without it
 * a custom factory silently drops every `placement:"root"` child spec.
 */
export function addTreeChildRows<R extends TreeRowLike>(
  result: SystemRowsResult,
  existingRows: readonly R[],
): void {
  for (const row of existingRows) {
    const childSpecs = row.childSpecs;
    if (childSpecs.length === 0) continue;
    const parentIdStr = RowNetworkKey.fromWire(row.id.toString());
    for (const spec of RowTypeSpecOps.resolveChildren(childSpecs, row.attributes())) {
      if (spec.placement !== "root") continue;
      result.rows.push(
        SystemRowDef.makeSpecRow(spec, parentIdStr, row.ownerId),
      );
    }
  }
}

/** Add a header-sub row if any row requires it for the given timescale. */
function applySecondHeaderRows<
  R extends {
    headerSecondRowKind?(ts: Timescale): Option<HeaderSecondRowKind>;
    requiresHeaderSecondRow(ts: Timescale): boolean;
  },
>(
  result: SystemRowsResult,
  rows: Iterable<R>,
  timescale: Timescale,
): void {
  if (timescale === "day") return;
  let selected: Option<HeaderSecondRowKind> = null;
  for (const row of rows) {
    const kind = headerSecondRowKindFor(row, timescale);
    if (!kind) continue;
    if (kind === "month-weekday") {
      selected = kind;
      break;
    }
    if (!selected) selected = kind;
  }
  if (selected) addSecondHeaderRow(result, selected);
}

/** Add secondary calendar rows as projected children of the header surface. */
function applySecondaryCalendars(
  result: SystemRowsResult,
  calendarSystems: readonly string[],
): void {
  const parent = SystemRowDef.headerProjectionParent(result);
  for (const system of calendarSystems) {
    const row = SystemRowDef.makeSecondaryCalendar(system);
    result.rows.push(row);
    if (parent) {
      SystemRowDef.appendPositionedChild(result, parent, row.id);
    }
  }
}

/**
 * The synthetic rows a sheet builds around its own: which to build and where
 * they sit.
 */
export namespace SystemRow {
  /**
   * Controls which system rows are built around the invariant
   * middle section (network-data-based children).
   * `prepareSystemRows` returns all static rows (headers +
   * footers — positions determine render order). `onRowsBuilt` is
   * a post-processing hook called after rows are assembled: adds
   * live tree children, adjusts header flags, etc. Inject into
   * `buildSystemRows`.
   */
  export interface Factory {
    /** Static system rows: headers + footers. */
    prepareSystemRows(config: SystemRowsConfig): SystemRowDef[];
    /** Post-processing: tree children + header flags + secondary calendars. */
    onRowsBuilt<R extends TreeRowLike>(
      result: SystemRowsResult,
      existingRows: readonly R[],
      timescale: Timescale,
      config: Option<SystemRowsConfig>,
    ): void;
  }

  export namespace Factory {
    /** Full sheet: header + footer + ephemeral label banner when applicable. */
    export const DEFAULT: Factory = deepFrozen({
      prepareSystemRows({ debugRows, mode, catalog }) {
        const headers = SystemRowDef.Static.HeaderRows;
        const footers = SystemRowDef.Static.Footers;
        const surfaces = SystemRowDef.Static.Surfaces;
        // The "unsaved" disclaimer is for user-minted ephemeral sheets;
        // builtin/reserved catalog sheets are ephemeral pre-promotion too
        // but live permanently in the sidebar, so they don't show it.
        // Template-derived sheets get the "Use this template" claim copy.
        const sheetMode = mode ?? "persisted";
        const showEphemeralDisclaimer = sheetMode !== "persisted" && catalog !== "builtin";
        const scrollInfo = debugRows?.includes("scroll-info") ?? false;
        const timelineLegend = debugRows?.includes("timeline-legend") ?? false;
        // Header surface-container wraps the existing synthetic
        // header system rows. The container itself is transparent
        // (cellHeight: 0); its children render in the sticky region.
        // On migrated sheets a server-backed surface-container at
        // top level supersedes this synthetic one — `replaceSystemRows`
        // detects that and reparents the children accordingly.
        const headerChildren: RowId[] = [headers.header.id];
        if (scrollInfo) headerChildren.push(headers.index.id);
        const headerSurface: SystemRowDef = {
          ...surfaces.header,
          children: headerChildren,
        };

        // Footer surface-container wraps every footer system row.
        const footerChildren: RowId[] = [];
        if (timelineLegend) {
          footerChildren.push(footers.timelineLegend.id);
        }
        if (showEphemeralDisclaimer) {
          footerChildren.push(footers.ephemeralDisclaimer.id);
        }
        footerChildren.push(footers.footer.id);
        const footerSurface: SystemRowDef = {
          ...surfaces.footer,
          children: footerChildren,
        };

        const rows: SystemRowDef[] = [
          headerSurface,
          {
            ...headers.header,
            position: SystemRowPosition.HEADER_PRIMARY,
          },
        ];
        if (scrollInfo) {
          rows.push({
            ...headers.index,
            position: SystemRowPosition.HEADER_AUXILIARY,
          });
        }
        rows.push(footerSurface);
        if (timelineLegend) {
          rows.push({
            ...footers.timelineLegend,
            position: SystemRowPosition.FOOTER_SURFACE_TIMELINE_LEGEND,
          });
        }
        if (showEphemeralDisclaimer) {
          rows.push({
            ...SystemRowDef.makeEphemeralBanner(sheetMode === "template" ? "template" : "blank"),
            position: SystemRowPosition.FOOTER_SURFACE_EPHEMERAL_DISCLAIMER,
          });
        }
        rows.push({
          ...footers.footer,
          position: SystemRowPosition.FOOTER_SURFACE_PRIMARY,
        });
        return rows;
      },
      onRowsBuilt(result, existingRows, timescale, config) {
        addTreeChildRows(result, existingRows);
        applySecondHeaderRows(result, existingRows, timescale);
        if (config?.calendarSystems?.length) {
          applySecondaryCalendars(result, config.calendarSystems);
        }
      },
    });

    /** Preview: header only, no footers — for null-state template previews. */
    export const PREVIEW: Factory = deepFrozen({
      prepareSystemRows: () => {
        const headers = SystemRowDef.Static.HeaderRows;
        const surfaces = SystemRowDef.Static.Surfaces;
        const headerSurface: SystemRowDef = {
          ...surfaces.header,
          children: [headers.header.id],
        };
        return [
          headerSurface,
          {
            ...headers.header,
            position: SystemRowPosition.HEADER_PRIMARY,
          },
        ];
      },
      onRowsBuilt(result, existingRows, timescale, config) {
        addTreeChildRows(result, existingRows);
        applySecondHeaderRows(result, existingRows, timescale);
        if (config?.calendarSystems?.length) {
          applySecondaryCalendars(result, config.calendarSystems);
        }
      },
    });
  }
}

/**
 * Build system rows from configuration.
 *
 * The factory controls which static rows (headers / footers)
 * surround the invariant middle (network-based root-placement
 * children). Defaults to `SystemRow.Factory.DEFAULT` when omitted.
 *
 * Call `factory.onRowsBuilt(result, existingRows, timeScale)`
 * separately to process live tree children and adjust header flags
 * once the full tree is assembled.
 */
function buildSystemRows(config: SystemRowsConfig): SystemRowsResult;
function buildSystemRows(
  config: SystemRowsConfig,
  factory: Option<SystemRow.Factory>,
): SystemRowsResult;
function buildSystemRows(
  config: SystemRowsConfig,
  factory: Option<SystemRow.Factory> = null,
): SystemRowsResult {
  const f = factory ?? SystemRow.Factory.DEFAULT;
  return {
    rows: [...f.prepareSystemRows(config), ...buildChildRows(config)],
  };
}

// ── SystemRows namespace ──────────────────────────────────────────

const $applySecondHeaderRows = applySecondHeaderRows;
const $applySecondaryCalendars = applySecondaryCalendars;

/**
 * Builds a sheet's system rows and applies its secondary header rows and
 * calendars.
 */
export namespace SystemRows {
  export const build = buildSystemRows;
  export const applySecondHeaderRows: <R extends { headerSecondRowKind?(ts: Timescale): Option<HeaderSecondRowKind>; requiresHeaderSecondRow(ts: Timescale): boolean; }>(result: SystemRowsResult, rows: Iterable<R>, timescale: Timescale) => void = $applySecondHeaderRows;
  export const applySecondaryCalendars: (result: SystemRowsResult, calendarSystems: readonly string[]) => void = $applySecondaryCalendars;
}
